import React from 'react';
import {
  IonPage,
  IonContent,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonBackButton,
  IonButton,
  IonIcon
} from '@ionic/react';
import { settingsOutline } from 'ionicons/icons';
import { useConfig } from '../hooks/useConfig';
import { useProgress } from '../hooks/useProgress';
import { useStreak } from '../hooks/useStreak';
import { useAchievements } from '../hooks/useAchievements';
import SEO from '../components/SEO';
import { seoService } from '../services/SEOService';
import './Profile.css';

/**
 * Profile Page - Beautiful Modern Design
 */
function Profile() {
  const { config } = useConfig();
  const meta = seoService.getPageMeta('profile');
  const { totalPoints = 0, lessonsCompleted = 0, challengesCompleted = 0 } = useProgress() || {};
  const { currentStreak = 0, longestStreak = 0 } = useStreak() || {};
  const { unlockedBadges = [], totalBadges = 0, unlockedCount = 0 } = useAchievements() || {};

  // Calculate level based on points
  const levels = config?.gamification?.levels || [
    { level: 1, name: 'Beginner', points: 0, icon: '🌱', color: '#81C784' },
    { level: 2, name: 'Learner', points: 500, icon: '🌿', color: '#66BB6A' },
    { level: 3, name: 'Coder', points: 1500, icon: '🌳', color: '#4CAF50' },
    { level: 4, name: 'Expert', points: 5000, icon: '🏆', color: '#FFA726' },
    { level: 5, name: 'Master', points: 10000, icon: '👑', color: '#FFD700' }
  ];

  const getCurrentLevel = () => {
    const pts = totalPoints || 0;
    for (let i = levels.length - 1; i >= 0; i--) {
      if (pts >= levels[i].points) {
        return levels[i];
      }
    }
    return levels[0];
  };

  const getNextLevel = () => {
    const current = getCurrentLevel();
    const nextIndex = levels.findIndex(l => l.level === current.level) + 1;
    return levels[nextIndex] || null;
  };

  const currentLevel = getCurrentLevel();
  const nextLevel = getNextLevel();
  const pts = totalPoints || 0;
  const progressToNext = nextLevel 
    ? Math.min(((pts - currentLevel.points) / (nextLevel.points - currentLevel.points)) * 100, 100)
    : 100;

  // Get recent badges (last 3 unlocked) - ensure they are proper objects
  const recentBadges = Array.isArray(unlockedBadges) 
    ? unlockedBadges.slice(-3).reverse().filter(b => b && typeof b === 'object')
    : [];

  return (
    <>
      <SEO
        title={meta.title}
        description={meta.description}
        keywords={meta.keywords}
        canonicalUrl={meta.canonicalUrl}
        ogImage={meta.ogImage}
      />
      <IonPage className="profile-page">
      <IonHeader className="ion-no-border">
        <IonToolbar className="profile-toolbar">
          <IonButtons slot="start">
            <IonBackButton defaultHref="/home" color="light" />
          </IonButtons>
          <IonTitle color="light">Profile</IonTitle>
          <IonButtons slot="end">
            <IonButton routerLink="/settings" fill="clear" color="light">
              <IonIcon icon={settingsOutline} />
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>

      <IonContent className="profile-content">
        {/* Hero Header with Gradient */}
        <div className="profile-hero">
          {/* Avatar Circle */}
          <div className="profile-avatar" style={{ borderColor: currentLevel.color }}>
            <span className="avatar-icon">{currentLevel.icon}</span>
          </div>

          {/* Level Badge */}
          <div className="level-badge" style={{ background: currentLevel.color }}>
            <span className="level-icon">{currentLevel.icon}</span>
            <span className="level-info">Level {currentLevel.level} • {currentLevel.name}</span>
          </div>

          {/* XP Display */}
          <div className="xp-display">
            <span className="xp-value">{(pts || 0).toLocaleString()}</span>
            <span className="xp-label">XP</span>
          </div>

          {/* Level Progress */}
          {nextLevel && (
            <div className="level-progress-container">
              <div className="level-progress-bar">
                <div 
                  className="level-progress-fill"
                  style={{ width: `${progressToNext}%` }}
                />
              </div>
              <div className="level-progress-text">
                <span>{Math.round(progressToNext)}%</span>
                <span>{((nextLevel.points - pts) || 0).toLocaleString()} XP to {nextLevel.name}</span>
              </div>
            </div>
          )}
          {!nextLevel && (
            <div className="max-level-badge">
              ⭐ Maximum Level Reached! ⭐
            </div>
          )}
        </div>

        {/* Stats Container */}
        <div className="profile-container">
          {/* Stats Grid */}
          <div className="stats-section">
            <h3 className="section-title">Your Stats</h3>
            <div className="stats-grid">
              <div className="stat-card">
                <div className="stat-icon">📚</div>
                <div className="stat-value">{lessonsCompleted || 0}</div>
                <div className="stat-label">Lessons</div>
              </div>
              
              <div className="stat-card">
                <div className="stat-icon">🎯</div>
                <div className="stat-value">{challengesCompleted || 0}</div>
                <div className="stat-label">Challenges</div>
              </div>
              
              <div className="stat-card">
                <div className="stat-icon">🔥</div>
                <div className="stat-value">{currentStreak || 0}</div>
                <div className="stat-label">Day Streak</div>
              </div>
              
              <div className="stat-card">
                <div className="stat-icon">🏆</div>
                <div className="stat-value">{unlockedCount || 0}</div>
                <div className="stat-label">Badges</div>
              </div>
            </div>
          </div>

          {/* Streak Section */}
          <div className="streak-section">
            <h3 className="section-title">Streak Progress</h3>
            <div className="streak-card">
              <div className="streak-main">
                <div className="streak-fire">
                  {(currentStreak || 0) > 0 ? '🔥' : '❄️'}
                </div>
                <div className="streak-info">
                  <span className="streak-count">{currentStreak || 0}</span>
                  <span className="streak-text">day{(currentStreak || 0) !== 1 ? 's' : ''} streak</span>
                </div>
              </div>
              <div className="streak-divider"></div>
              <div className="streak-best">
                <span className="best-label">🏅 Personal Best</span>
                <span className="best-value">{longestStreak || 0} days</span>
              </div>
              {(currentStreak || 0) > 0 && (
                <div className="streak-message">
                  {currentStreak >= 7 ? "🎉 Amazing streak! Keep it up!" : 
                   currentStreak >= 3 ? "💪 You're on fire! Don't break the chain!" :
                   "🌟 Great start! Come back tomorrow!"}
                </div>
              )}
              {(currentStreak || 0) === 0 && (
                <div className="streak-message warning">
                  📖 Complete a lesson today to start your streak!
                </div>
              )}
            </div>
          </div>

          {/* Recent Badges */}
          {recentBadges.length > 0 && (
            <div className="badges-section">
              <div className="section-header">
                <h3 className="section-title">Recent Badges</h3>
                <IonButton fill="clear" size="small" routerLink="/achievements">
                  View All →
                </IonButton>
              </div>
              <div className="badges-preview">
                {recentBadges.map((badge, index) => (
                  <div key={badge?.id || index} className="badge-preview-item">
                    <div className="badge-icon-wrapper">
                      {badge?.image ? (
                        <img 
                          src={badge.image.startsWith('/') ? badge.image : `/assets/media/${badge.image}`} 
                          alt={badge?.name || 'Badge'}
                          className="badge-img"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.parentElement.innerHTML = '<span class="badge-emoji">🏅</span>';
                          }}
                        />
                      ) : (
                        <span className="badge-emoji">🏅</span>
                      )}
                    </div>
                    <span className="badge-name">{badge?.name || 'Badge'}</span>
                  </div>
                ))}
              </div>
              <div className="badges-progress">
                <div className="badges-progress-bar">
                  <div 
                    className="badges-progress-fill"
                    style={{ width: `${totalBadges > 0 ? ((unlockedCount || 0) / totalBadges) * 100 : 0}%` }}
                  />
                </div>
                <span className="badges-progress-text">
                  {unlockedCount || 0} / {totalBadges || 0} badges unlocked
                </span>
              </div>
            </div>
          )}

          {/* Quick Actions */}
          <div className="actions-section">
            <IonButton expand="block" className="action-btn primary" routerLink="/achievements">
              🏆 View All Achievements
            </IonButton>
            <IonButton expand="block" className="action-btn secondary" fill="outline" routerLink="/settings">
              ⚙️ Settings
            </IonButton>
            <IonButton expand="block" className="action-btn tertiary" fill="outline" routerLink="/help">
              ❓ Help & Support
            </IonButton>
          </div>

          {/* Language Info */}
          <div className="language-info">
            <span className="language-label">Learning</span>
            <span className="language-name">{config?.app?.language?.toUpperCase() || 'CODE'}</span>
          </div>
        </div>
      </IonContent>
    </IonPage>
    </>
  );
}

export default Profile;